import { useEffect, useState } from 'react';
import { useFutureOrderStore } from '../../components/Stores/FutureOrdersStore';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../components/Stores/accountStore';
import ButtonDesigned from '../../components/UI-Elements/Buttons/ButtonDesigned';
import { useFutureOrders } from '../../hooks/useFutureOrders';
import { FutureOrdersSummeryGraphs } from './FutureOrders/FutureOrdersSummeryGraphs/FutureOrdersSummeryGraphs';
import { MaterialTrendButton } from '../Dashboard/MaterialTrendButton';
import { getMaterialsCardData } from '../Dashboard/dashboardUtils';
import { getFutureOrderExposedMaterials } from './FutureOrders/Utils';
import { useExposureStore } from '../../components/Stores/exposureDataStore';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
import { useUser } from '@hooks/useUser';
import { CommitmentsList } from './SalesCommComponents/CommitmentsList';
import { AddSalesCommitmentPopup } from './SalesCommComponents/AddSalesCommitmentPopup';
import { OverlayDisplayDiv } from '../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { IFutureOrder } from '../../sp-types/FutureOrderType';

export interface materialExposureDictionary {
  totalValue: number;
  totalWeight: number;
  exposureCalendar: Record<string, number>;
  price?: number;
}

export const SalesCommitments = () => {
  const { account, materialColorDictionary: colorsMap } = useAccountStore();
  const { data: user } = useUser();
  const { materials, isMaterialsLoading, materialsMap } = useMaterialsStore();
  const {
    netExposure,

    currentSelectedMaterialId: materialId,
    setCurrentSelectedMaterialId,
  } = useExposureStore();
  const { convertionMap, pricesMap } = useCurrencyRates();

  const { refetch: refetchOrders, isLoading: isFutureOrderQueryLoading } = useFutureOrders();
  const { allFutureOrders, setIsFutureOrdersLoading, isFutureOrdersLoading } = useFutureOrderStore();
  const [dataStatus, setDataStatus] = useState<'loading' | 'missing' | 'fetched'>('missing');
  const allFutureOrdersMaterials = getFutureOrderExposedMaterials(allFutureOrders);
  const [showAddPopup, setShowAddPopup] = useState<boolean>(false);
  const [editingOrder, setEditingOrder] = useState<IFutureOrder | undefined>(undefined);

  useEffect(() => {
    if (isMaterialsLoading || isFutureOrdersLoading) {
      setDataStatus('loading');
    } else if (materials.length > 0 && allFutureOrders.length > 0) {
      setDataStatus('fetched');
    } else {
      setDataStatus('missing');
    }
  }, [isMaterialsLoading, isFutureOrdersLoading, materials, allFutureOrders]);

  useEffect(() => {
    account.id && refetchOrders();
  }, [account]);

  useEffect(() => {
    setIsFutureOrdersLoading(isFutureOrderQueryLoading);
  }, [isFutureOrderQueryLoading]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between">
        <div>
          <MaterialTrendButton
            buttons={getMaterialsCardData(
              materialsMap,
              colorsMap,
              convertionMap,
              account.materialsToAccount.filter(
                (item) => allFutureOrdersMaterials.includes(item.materialId) || item.materialId === 'general',
              ),
              netExposure,
            )}
            currentMaterial={materialId}
            setCurrentMaterial={setCurrentSelectedMaterialId}
          />
        </div>
        {user?.isEditEnabled && (
          <ButtonDesigned style="primary" onButtonClick={() => setShowAddPopup(true)}>
            Create commitment
          </ButtonDesigned>
        )}
      </div>
      <div>
        <FutureOrdersSummeryGraphs
          componentDataState={dataStatus}
          futureOrders={allFutureOrders}
          currentMaterialId={materialId}
        />
      </div>
      <div className="flex flex-col flex-1 grow ">
        <CommitmentsList
          {...{ allFutureOrders, materialId, refetchOrders, materialsMap, pricesMap }}
          isEditEnabled={!!user?.isEditEnabled}
          setLoading={setIsFutureOrdersLoading}
          colors={colorsMap}
          isAdmin={!!user?.isAdmin}
          onEditCommitment={(order) => {
            setEditingOrder(order);
            setShowAddPopup(true);
          }}
        />
      </div>

      {showAddPopup && (
        <OverlayDisplayDiv setDisplayOverlay={() => setShowAddPopup(false)} hideClose>
          <AddSalesCommitmentPopup
            cancel={() => {
              setShowAddPopup(false);
              setEditingOrder(undefined);
            }}
            onCommitmentAdded={() => {
              setShowAddPopup(false);
              setEditingOrder(undefined);
              refetchOrders();
            }}
            isUpdate={!!editingOrder}
            updateRow={editingOrder}
          />
        </OverlayDisplayDiv>
      )}
    </div>
  );
};
